import React, { useEffect, useState } from 'react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * 2026-02 — Concierge Onboarding amber banner.
 *
 * Reads `onboarding_status` from `/api/agency/profile`. When the agent
 * has clicked "Skip Setup — Onboarding Concierge Will Handle This" in
 * the onboarding wizard the backend stamps
 * `onboarding_status='concierge_in_progress'` on the AgentClient. This
 * banner reads that flag and renders a persistent amber notice on the
 * dashboard until the concierge marks the account as completed (a
 * future workflow step).
 *
 * The banner is read-only — no dismiss button — because the concierge
 * status is owned by the concierge team, not the agent.
 */
export default function ConciergeBanner() {
  const [active, setActive] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/agency/profile`, {
          credentials: 'include',
        });
        if (!res.ok || cancelled) return;
        const p = await res.json();
        if (!cancelled) {
          setActive(p.onboarding_status === 'concierge_in_progress');
        }
      } catch {
        // Non-fatal — banner stays hidden if the request fails.
      }
    };
    load();
    // Poll every 60s so the banner clears automatically once the
    // concierge team marks the account complete elsewhere.
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!active) return null;

  return (
    <div
      data-testid="concierge-banner"
      role="status"
      style={{
        background: '#fffbeb',          // amber-50
        border: '1px solid #fcd34d',    // amber-300
        color: '#92400e',               // amber-800
        borderRadius: 10,
        padding: '0.75rem 1rem',
        marginBottom: 0,
        display: 'flex',
        alignItems: 'center',
        gap: '0.6rem',
        fontSize: '0.95rem',
        fontWeight: 500,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 22, height: 22, borderRadius: '50%',
          background: '#f59e0b', color: '#fff',
          fontWeight: 700, fontSize: '0.85rem',
        }}
      >
        ⓘ
      </span>
      <span>
        Onboarding in progress — your concierge is setting up your account.
      </span>
    </div>
  );
}
